export const M5_UNIT_4 = {
  id: "m5-u4-past-regular",
  title: "Unit 4: Past Simple - Regular Verbs",
  topic: "Talking about what you did yesterday, last week, and in the past",
  iconType: "calendar-check",
  grammarImage: "/grammar/grammar-m5-u4.jpg",
  grammarText: "Regular verbs in the past simple end in -ed (walked, played, watched). The form is the same for all subjects: I walked, she walked, they walked. For negatives, use didn't + base verb (didn't walk). For questions, use Did + subject + base verb (Did you walk?). We use the past simple to talk about completed actions in the past, often with time expressions like yesterday, last night, last week, or in 2020. This tense helps you tell stories, describe what you did, and talk about your experiences.",

  sets: [
    // ===================================================================
    // SET 1: EXPOSICIÓN PASIVA - READING SIMPLE STORIES (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u4-s1",
      title: "Set 1: Recognizing Past Actions",
      questions: [
        {
          type: "match",
          q: "READ: Match the activities to their context:",
          pairs: [
            { left: "I watched Netflix for 5 hours", right: "Entertainment" },
            { left: "She cooked dinner at 7pm", right: "Food preparation" },
            { left: "They played video games", right: "Gaming" }
          ],
          a: "completed",
          successtip: "✓ Notice the pattern: watch→watched, cook→cooked, play→played! Past actions! 🎬🍳🎮"
        },
        {
          type: "mc",
          q: "READ: 'Yesterday I listened to Spotify.' When did this happen?",
          options: ["Yesterday (past)", "Now (present)", "Tomorrow (future)"],
          a: "Yesterday (past)",
          successtip: "✓ YESTERDAY = past! 'Listened' ends in -ed! 🎵"
        },
        {
          type: "match",
          q: "READ: Match present to past:",
          pairs: [
            { left: "I walk to work (now)", right: "I walked to work (yesterday)" },
            { left: "She talks on the phone (now)", right: "She talked on the phone (yesterday)" },
            { left: "They work from home (now)", right: "They worked from home (yesterday)" }
          ],
          a: "completed",
          tip: "Present → Past: just add -ed to regular verbs! 🔄"
        },
        {
          type: "mc",
          q: "READ: 'Last night I watched a movie. Now I'm watching a series.' Which is PAST?",
          options: ["Watched (last night)", "Watching (now)", "Both"],
          a: "Watched (last night)",
          successtip: "✓ WATCHED = past! Watching = present continuous! ⏰"
        },
        {
          type: "input",
          q: "READ: 'He posted on Instagram yesterday.' The verb 'posted' talks about the ___.",
          a: "past",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ PAST! Posted = past form of 'post'! 📱"
        },
        {
          type: "match",
          q: "READ: Daily life then vs now:",
          pairs: [
            { left: "I cleaned my room (Saturday)", right: "Past action" },
            { left: "I'm cleaning now", right: "Present action" },
            { left: "I clean every day", right: "Habit/routine" }
          ],
          a: "completed",
          successtip: "✓ Three different times: past, present continuous, present simple! 🧹"
        },
        {
          type: "mc",
          q: "READ: 'She usually walks to school, but yesterday she walked in the rain.' How many times does 'walk' appear?",
          options: ["Twice (walks + walked)", "Once", "Three times"],
          a: "Twice (walks + walked)",
          successtip: "✓ WALKS (present habit) + WALKED (past action)! Same verb, different times! 🚶‍♀️"
        },
        {
          type: "input",
          q: "READ: 'Last Friday we played soccer. We play every Friday.' Which form is past? ___",
          a: "played",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ PLAYED! 'Play' = present, 'played' = past! ⚽"
        },
        {
          type: "mc",
          q: "READ MINI-STORY: 'Yesterday started normal. I woke up, checked my phone, and worked from home. Then something funny happened—my cat walked across my keyboard during a Zoom meeting!' What did the cat do?",
          options: ["Walked across the keyboard", "Sleeps on the desk", "Is walking now"],
          a: "Walked across the keyboard",
          successtip: "✓ The cat WALKED! Funny moment! 🐱💻"
        },
        {
          type: "match",
          q: "READ: Match the time expressions:",
          pairs: [
            { left: "Yesterday", right: "Past" },
            { left: "Last week", right: "Past" },
            { left: "Right now", right: "Present" }
          ],
          a: "completed",
          successtip: "✓ Yesterday/last week = past time markers! 📅"
        }
      ]
    },

    // ===================================================================
    // SET 2: PRESENTE → PASADO BASIC (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u4-s2",
      title: "Set 2: From Now to Then",
      questions: [
        {
          type: "mc",
          q: "NOW: I watch YouTube every day. YESTERDAY: I ___ YouTube for 3 hours.",
          options: ["watched", "watch", "watching"],
          a: "watched",
          successtip: "✓ WATCHED! Present: watch → Past: watched! 🎥"
        },
        {
          type: "input",
          q: "NOW: She listens to podcasts. LAST WEEK: She ___ to 5 episodes.",
          a: "listened",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Regular verbs: listen → listened (add -ed!)",
          successtip: "✓ LISTENED! Add -ed for past! 🎧"
        },
        {
          type: "mc",
          q: "CONFUSING: She ___ on TikTok right now. (present continuous)",
          options: ["is scrolling", "scrolled", "scrolls"],
          a: "is scrolling",
          successtip: "✓ IS SCROLLING! Present continuous = happening now! 📱"
        },
        {
          type: "order",
          q: "Traduce: Ayer mi mamá paseó al perro.",
          words: ["Yesterday", "my", "mom", "walked", "the", "dog"],
          a: "Yesterday my mom walked the dog",
          tip: "Walk = caminar o pasear 🐕",
          successtip: "✓ Walk = caminar/pasear! Walked = past! 🐕"
        },
        {
          type: "input",
          q: "NOW: They work in an office. LAST YEAR: They ___ from home.",
          a: "worked",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WORKED! Remote work was the norm! 💼🏠"
        },
        {
          type: "mc",
          q: "CONFUSING: I ___ dinner right now, but yesterday I ordered Uber Eats.",
          options: ["am cooking", "cooked", "cook"],
          a: "am cooking",
          successtip: "✓ AM COOKING (now) vs ORDERED (yesterday)! 🍳📦"
        },
        {
          type: "match",
          q: "Match present to past:",
          pairs: [
            { left: "I play (habit)", right: "I played (yesterday)" },
            { left: "She studies (routine)", right: "She studied (last night)" },
            { left: "They live (current)", right: "They lived (before)" }
          ],
          a: "completed",
          successtip: "✓ Simple transformation: add -ed! ✅"
        },
        {
          type: "input",
          q: "NOW: He checks his email constantly. THIS MORNING: He ___ his email 10 times!",
          a: "checked",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ CHECKED! Too many emails! 📧"
        },
        {
          type: "order",
          q: "Traduce: Anoche miré una serie en Netflix.",
          words: ["Last", "night", "I", "watched", "a", "series", "on", "Netflix"],
          a: "Last night I watched a series on Netflix",
          successtip: "✓ Binge-watching! Watched = past of watch! 📺"
        },
        {
          type: "mc",
          q: "STORY: Last weekend I ___ my room, ___ some music, and ___ with friends online.",
          options: ["cleaned / played / talked", "clean / play / talk", "cleaning / playing / talking"],
          a: "cleaned / played / talked",
          successtip: "✓ All past! Cleaned, played, talked! Productive weekend! 🎉"
        }
      ]
    },

    // ===================================================================
    // SET 3: DAILY VERBS + TECH CONTEXT (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u4-s3",
      title: "Set 3: Common Daily Actions",
      questions: [
        {
          type: "match",
          q: "Match tech actions (past):",
          pairs: [
            { left: "I posted a photo", right: "Social media" },
            { left: "She downloaded an app", right: "Phone/tablet" },
            { left: "They streamed a concert", right: "Entertainment" }
          ],
          a: "completed",
          successtip: "✓ Modern past actions! Posted, downloaded, streamed! 📱🎵"
        },
        {
          type: "mc",
          q: "Yesterday I ___ 100 photos from my vacation to Instagram.",
          options: ["uploaded", "upload", "uploading"],
          a: "uploaded",
          successtip: "✓ UPLOADED! Past tense! Too many photos! 📸"
        },
        {
          type: "input",
          q: "Last night she ___ the latest episode the second it dropped. (watch)",
          a: "watched",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WATCHED! True fan behavior! 📺"
        },
        {
          type: "order",
          q: "Traduce: El año pasado trabajé en una cafetería.",
          words: ["Last", "year", "I", "worked", "in", "a", "coffee", "shop"],
          a: "Last year I worked in a coffee shop",
          successtip: "✓ Common student job! ☕💼"
        },
        {
          type: "mc",
          q: "CONFUSING: Right now I ___ my homework, but yesterday I ___ video games all day.",
          options: ["am finishing / played", "finish / play", "finished / am playing"],
          a: "am finishing / played",
          successtip: "✓ AM FINISHING (now) vs PLAYED (yesterday)! Priorities! 📚🎮"
        },
        {
          type: "input",
          q: "My grandparents ___ me a letter last month. It was so sweet! (mail/send)",
          a: "mailed",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["sent", "mailed"] },
          successtip: "✓ MAILED! Old-school but lovely! 💌"
        },
        {
          type: "match",
          q: "Traditional vs Modern actions:",
          pairs: [
            { left: "She called me (phone)", right: "Traditional" },
            { left: "He texted me", right: "Modern" },
            { left: "They emailed the document", right: "Modern" }
          ],
          a: "completed",
          successtip: "✓ Mix of old and new communication! 📞📱"
        },
        {
          type: "input",
          q: "Last weekend we ___ a birthday party for my sister. (organize)",
          a: "organized",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["organised"] },
          tip: "Organize ends in 'e', so just add -d!",
          successtip: "✓ ORGANIZED! US spelling. UK = organised! 🎂🎉"
        },
        {
          type: "order",
          q: "Traduce: Ayer limpié mi habitación y ayudé a mi mamá.",
          words: ["Yesterday", "I", "cleaned", "my", "room", "and", "helped", "my", "mom"],
          a: "Yesterday I cleaned my room and helped my mom",
          successtip: "✓ Two past actions with 'and'! Good kid! 🧹❤️"
        },
        {
          type: "mc",
          q: "MINI-STORY: 'Last week was crazy! I worked 50 hours, cooked all my meals, and still exercised every morning. I was exhausted!' What did the person do?",
          options: ["Worked, cooked, and exercised", "Is working now", "Will work next week"],
          a: "Worked, cooked, and exercised",
          successtip: "✓ Three past actions! Super productive (and tired)! 😴💪"
        }
      ]
    },

    // ===================================================================
    // SET 4: INTRODUCING NEGATIVES (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u4-s4",
      title: "Set 4: What Didn't Happen",
      questions: [
        {
          type: "match",
          q: "Match affirmative to negative:",
          pairs: [
            { left: "I watched TV", right: "I didn't watch TV" },
            { left: "She played games", right: "She didn't play games" },
            { left: "They worked", right: "They didn't work" }
          ],
          a: "completed",
          tip: "Negative: didn't + base verb (no -ed!)",
          successtip: "✓ Didn't + base verb! The -ed disappears! ❌"
        },
        {
          type: "mc",
          q: "I wanted to go to the party, but I ___ because I was sick.",
          options: ["didn't go", "didn't went", "not went"],
          a: "didn't go",
          tip: "didn't + BASE verb (go, not went!)",
          successtip: "✓ DIDN'T GO! Never 'didn't went'! Common mistake! ⚠️"
        },
        {
          type: "input",
          q: "She posted on Instagram, but she ___ anything on Facebook because she deleted her account. (post)",
          a: "didn't post",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["did not post"] },
          successtip: "✓ DIDN'T POST! Social media cleanse! 📱🚫"
        },
        {
          type: "order",
          q: "Traduce: No estudié para el examen porque estaba cansado.",
          words: ["I", "didn't", "study", "for", "the", "exam", "because", "I", "was", "tired"],
          a: "I didn't study for the exam because I was tired",
          tip: "didn't + study (base form!)",
          successtip: "✓ Bad decision! Should have studied! 📚😴"
        },
        {
          type: "mc",
          q: "CONFUSING: Yesterday I played soccer, but I ___ basketball because the court was closed.",
          options: ["didn't play", "don't play", "not played"],
          a: "didn't play",
          successtip: "✓ DIDN'T PLAY! One thing happened, another didn't! ⚽🏀"
        },
        {
          type: "input",
          q: "We watched the movie, but we ___ the ending because we fell asleep. (understand)",
          a: "didn't understand",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["did not understand"] },
          successtip: "✓ DIDN'T UNDERSTAND! Too boring or too late! 😴🎬"
        },
        {
          type: "match",
          q: "Positive vs Negative situations:",
          pairs: [
            { left: "I liked the concert", right: "Enjoyed it" },
            { left: "I didn't like the concert", right: "Didn't enjoy it" },
            { left: "I loved it!", right: "Really enjoyed it!" }
          ],
          a: "completed",
          successtip: "✓ Liked vs didn't like = opposite reactions! 👍👎"
        },
        {
          type: "input",
          q: "He wanted to cook, but he ___ because he burned everything last time. (try)",
          a: "didn't try",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["did not try"] },
          tip: "Try → tried (past), but: didn't + try (base form!)",
          successtip: "✓ DIDN'T TRY! Safety first! 🔥🍳"
        },
        {
          type: "order",
          q: "Traduce: Mi amigo no contestó mis mensajes porque estaba en el cine.",
          words: ["My", "friend", "didn't", "answer", "my", "messages", "because", "he", "was", "at", "the", "movies"],
          a: "My friend didn't answer my messages because he was at the movies",
          successtip: "✓ Valid excuse! Movies = phone off! 📱🎬"
        },
        {
          type: "mc",
          q: "STORY: 'I planned a perfect day. I wanted to go to the beach, but it rained. I didn't go. Instead, I stayed home and watched movies.' What happened?",
          options: ["Plans changed because of rain", "Went to the beach anyway", "Is at the beach now"],
          a: "Plans changed because of rain",
          successtip: "✓ DIDN'T GO! Life happens! Rainy day indoors! 🌧️🏖️"
        }
      ]
    },

    // ===================================================================
    // SET 5: TIME EXPRESSIONS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u4-s5",
      title: "Set 5: When Did It Happen?",
      questions: [
        {
          type: "match",
          q: "Match time expressions to examples:",
          pairs: [
            { left: "Yesterday", right: "I worked from home yesterday" },
            { left: "Last night", right: "She watched TV last night" },
            { left: "Last week", right: "They traveled last week" }
          ],
          a: "completed",
          successtip: "✓ Time markers help identify past simple! ⏰"
        },
        {
          type: "mc",
          q: "___ I binge-watched an entire season of Breaking Bad.",
          options: ["Last weekend", "Right now", "Tomorrow"],
          a: "Last weekend",
          successtip: "✓ LAST WEEKEND! Epic binge! 📺🍿"
        },
        {
          type: "input",
          q: "___ morning I checked my email 20 times before lunch. (This/That/Yesterday)",
          a: "This",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Yesterday"] },
          successtip: "✓ THIS MORNING! Earlier today = past! 📧"
        },
        {
          type: "order",
          q: "Traduce: La semana pasada empecé un nuevo trabajo.",
          words: ["Last", "week", "I", "started", "a", "new", "job"],
          a: "Last week I started a new job",
          tip: "Start → started (just add -ed!)",
          successtip: "✓ New beginnings! Congrats! 💼🎉"
        },
        {
          type: "mc",
          q: "CONFUSING: ___ I'm studying for my exam, but ___ I played video games all day.",
          options: ["Right now / yesterday", "Yesterday / right now", "Tomorrow / today"],
          a: "Right now / yesterday",
          successtip: "✓ Contrasting NOW (continuous) vs YESTERDAY (past)! 📚🎮"
        },
        {
          type: "input",
          q: "In 2020 everyone ___ from home because of the pandemic. (work)",
          a: "worked",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WORKED! 2020 = specific past year! Remote work era! 🏠💻"
        },
        {
          type: "match",
          q: "Time expressions - near vs far past:",
          pairs: [
            { left: "Yesterday", right: "Recent past" },
            { left: "Last year", right: "Further past" },
            { left: "In 2010", right: "Distant past" }
          ],
          a: "completed",
          successtip: "✓ All are past, but different distances from now! 📅"
        },
        {
          type: "input",
          q: "Three days ___ I downloaded TikTok. Now I'm addicted! (time word)",
          a: "ago",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ AGO! Time + ago = how long before now! 📱⏰"
        },
        {
          type: "order",
          q: "Traduce: Hace dos años viví en Nueva York.",
          words: ["Two", "years", "ago", "I", "lived", "in", "New", "York"],
          a: "Two years ago I lived in New York",
          tip: "Live → lived (add -d because it ends in 'e'!)",
          successtip: "✓ AGO goes at the start or end! 🗽"
        },
        {
          type: "mc",
          q: "MINI-STORY: 'Yesterday started badly. This morning I missed my bus. Last night I didn't sleep well. But three hours ago everything changed—I got a job offer!' When did they get the offer?",
          options: ["Three hours ago", "Yesterday", "Last night"],
          a: "Three hours ago",
          successtip: "✓ THREE HOURS AGO! Happy ending! 💼🎉"
        }
      ]
    },

    // ===================================================================
    // SET 6: SPELLING PATTERNS SUBTLE (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u4-s6",
      title: "Set 6: Different Verb Endings",
      questions: [
        {
          type: "match",
          q: "Match the verbs to their past forms:",
          pairs: [
            { left: "dance", right: "danced" },
            { left: "study", right: "studied" },
            { left: "stop", right: "stopped" }
          ],
          a: "completed",
          tip: "Notice: dance (ends in 'e') → just add -d. Study (ends in 'y') → change to -ied. Stop → double consonant + -ed!",
          successtip: "✓ Three different patterns! Dance/Study/Stop! 💃📚🛑"
        },
        {
          type: "mc",
          q: "Yesterday I ___ for 5 hours straight for my exam. (study)",
          options: ["studied", "studyed", "studyied"],
          a: "studied",
          tip: "When a verb ends in consonant + y, change y → ied!",
          successtip: "✓ STUDIED! Y changes to I + ED! Don't say 'stud-eed', say 'STUH-deed'! 📚"
        },
        {
          type: "input",
          q: "Last weekend she ___ to her favorite song 10 times! (dance)",
          a: "danced",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Dance ends in 'e', so just add -d!",
          successtip: "✓ DANCED! Ends in 'e' = just add -d! Not 'dan-sed', say 'DANST'! 💃"
        },
        {
          type: "order",
          q: "Traduce: Ayer intenté cocinar pasta pero la quemé.",
          words: ["Yesterday", "I", "tried", "to", "cook", "pasta", "but", "I", "burned", "it"],
          a: "Yesterday I tried to cook pasta but I burned it",
          tip: "Try → tried (y changes to i!). Burn → burned (just add -ed!)",
          successtip: "✓ TRIED! Cooking fails happen! 🍝🔥"
        },
        {
          type: "mc",
          q: "The music was so loud that everyone ___ dancing immediately. (stop)",
          options: ["stopped", "stoped", "stopped"],
          a: "stopped",
          tip: "One syllable + vowel + consonant = double the last consonant!",
          successtip: "✓ STOPPED! Double the P! Not 'stop-ed', say 'STOPT'! 🎵🛑"
        },
        {
          type: "input",
          q: "Last night I ___ a taxi because the subway was closed. (call)",
          a: "called",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ CALLED! Most verbs = just add -ed! Easy! 🚕"
        },
        {
          type: "match",
          q: "Pronunciation guide:",
          pairs: [
            { left: "worked", right: "sounds like 'WORKT'" },
            { left: "played", right: "sounds like 'PLAYD'" },
            { left: "wanted", right: "sounds like 'WON-tid'" }
          ],
          a: "completed",
          tip: "Three sounds: /t/ (worked), /d/ (played), /id/ (wanted)!",
          successtip: "✓ Don't say 'work-ED'! Say 'WORKT'! Natural pronunciation! 🗣️"
        },
        {
          type: "input",
          q: "She ___ all her old photos to the cloud last month. (copy)",
          a: "copied",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Copy → copied (y changes to ied!)",
          successtip: "✓ COPIED! Y → IED pattern! ☁️📸"
        },
        {
          type: "order",
          q: "Traduce: Planeamos unas vacaciones pero cancelamos todo.",
          words: ["We", "planned", "a", "vacation", "but", "we", "canceled", "everything"],
          a: "We planned a vacation but we canceled everything",
          tip: "Plan → planned (double the 'n'!). Cancel → canceled (US spelling, just add -ed!)",
          successtip: "✓ PLANNED! US = canceled (one L), UK = cancelled (two Ls)! 🏖️❌"
        },
        {
          type: "mc",
          q: "CHALLENGE: She usually ___ messages quickly, but yesterday she ___ for an hour before replying. (reply)",
          options: ["replies / waited", "replied / waits", "replying / waited"],
          a: "replies / waited",
          tip: "Present: replies (habit). Past: waited!",
          successtip: "✓ REPLIES (present) vs WAITED (past)! Two different verbs! 📱⏰"
        }
      ]
    },

    // ===================================================================
    // SET 7: YES/NO QUESTIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u4-s7",
      title: "Set 7: Asking About the Past",
      questions: [
        {
          type: "match",
          q: "Match questions to natural short answers:",
          pairs: [
            { left: "Did you watch the game?", right: "Yes, I did / No, I didn't" },
            { left: "Did she call you?", right: "Yes, she did / No, she didn't" },
            { left: "Did they work yesterday?", right: "Yes, they did / No, they didn't" }
          ],
          a: "completed",
          tip: "Questions: Did + subject + base verb? Answers: Yes/No + did/didn't!",
          successtip: "✓ Natural short answers! 'Yes, I did' or 'No, I didn't'! 💬"
        },
        {
          type: "mc",
          q: "___ you watch the finale last night?",
          options: ["Did", "Do", "Are"],
          a: "Did",
          tip: "Past simple questions start with 'Did'!",
          successtip: "✓ DID! Natural answer: 'Yes, I did! It was amazing!' 📺"
        },
        {
          type: "input",
          q: "___ she post the photos on Instagram? (question word)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ DID! Answer: 'Yes, she did! Check her profile!' 📸"
        },
        {
          type: "order",
          q: "Traduce: ¿Estudiaste para el examen?",
          words: ["Did", "you", "study", "for", "the", "exam?"],
          a: "Did you study for the exam?",
          tip: "Did + subject + BASE verb (study, not studied!)",
          successtip: "✓ Natural response: 'Yes, I did' or 'No, I didn't, I forgot!' 📚"
        },
        {
          type: "mc",
          q: "A: 'Did you enjoy the concert?' B: '___'",
          options: ["Yes, I did! It was incredible!", "Yes, I enjoyed", "Yes, I do"],
          a: "Yes, I did! It was incredible!",
          successtip: "✓ Full natural answer! 'Yes, I did' + extra info! 🎵✨"
        },
        {
          type: "input",
          q: "A: 'Did they finish the project?' B: 'No, they ___.'",
          a: "didn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["did not"] },
          successtip: "✓ DIDN'T! Short negative answer! Can add: 'They ran out of time.' ⏰"
        },
        {
          type: "match",
          q: "Questions about weekend:",
          pairs: [
            { left: "Did you relax?", right: "Yes, I slept all day" },
            { left: "Did you go out?", right: "No, I stayed home" },
            { left: "Did you have fun?", right: "Yes, I visited friends" }
          ],
          a: "completed",
          successtip: "✓ Weekend conversations! Common questions! 🏠🎉"
        },
        {
          type: "input",
          q: "A: 'Did he reply to your message?' B: 'Yes, he ___, but very late!'",
          a: "did",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ DID! Adding context: 'Yes, he did, but at 2am!' 📱"
        },
        {
          type: "order",
          q: "Traduce: ¿Cocinaste ayer o pediste comida?",
          words: ["Did", "you", "cook", "yesterday", "or", "did", "you", "order", "food?"],
          a: "Did you cook yesterday or did you order food?",
          successtip: "✓ Two options question! 'Did you... or did you...?' 🍳📦"
        },
        {
          type: "mc",
          q: "CONVERSATION: A: 'Did you like the movie?' B: 'Not really. I ___ it boring.' A: 'Really? I loved it!'",
          options: ["found", "find", "founded"],
          a: "found",
          tip: "Find → found (irregular!), but we're learning regular verbs, so think: 'I thought it was boring'!",
          successtip: "✓ Different opinions! 'I found it boring' = I thought it was boring! 🎬"
        }
      ]
    },

    // ===================================================================
    // SET 8: WH- QUESTIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u4-s8",
      title: "Set 8: Information Questions",
      questions: [
        {
          type: "match",
          q: "Match question words to what they ask:",
          pairs: [
            { left: "What did you watch?", right: "Thing/activity" },
            { left: "Where did you work?", right: "Location" },
            { left: "When did you start?", right: "Time" }
          ],
          a: "completed",
          successtip: "✓ What/Where/When + did + subject + base verb! 🤔"
        },
        {
          type: "mc",
          q: "___ did you watch last night?",
          options: ["What", "Where", "Who"],
          a: "What",
          successtip: "✓ WHAT = asking about the show/movie! Answer: 'I watched Stranger Things!' 📺"
        },
        {
          type: "input",
          q: "___ did she work last year? (question word for location)",
          a: "Where",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WHERE! Answer: 'She worked at Google' or 'She worked from home'! 💼"
        },
        {
          type: "order",
          q: "Traduce: ¿Cuándo empezaste tu nuevo trabajo?",
          words: ["When", "did", "you", "start", "your", "new", "job?"],
          a: "When did you start your new job?",
          successtip: "✓ Answer: 'I started last Monday!' 📅"
        },
        {
          type: "mc",
          q: "A: '___ did you study?' B: 'I studied at the library for 5 hours!'",
          options: ["Where", "What", "When"],
          a: "Where",
          successtip: "✓ WHERE! Could also ask 'How long' for duration! 📚"
        },
        {
          type: "input",
          q: "A: '___ did you listen to?' B: 'I listened to that new Taylor Swift album!'",
          a: "What",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WHAT! Music question! 🎵"
        },
        {
          type: "match",
          q: "Match questions to their answers:",
          pairs: [
            { left: "What did you cook?", right: "I cooked pasta" },
            { left: "Why did you call?", right: "I called because I needed help" },
            { left: "How did you travel?", right: "I traveled by bus" }
          ],
          a: "completed",
          successtip: "✓ Different question words = different info! 🍝🚌"
        },
        {
          type: "input",
          q: "A: '___ did you arrive?' B: 'I arrived at 10pm!'",
          a: "When",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["What time"] },
          successtip: "✓ WHEN or WHAT TIME! Both work! ⏰"
        },
        {
          type: "order",
          q: "Traduce: ¿Por qué cancelaste la reunión?",
          words: ["Why", "did", "you", "cancel", "the", "meeting?"],
          a: "Why did you cancel the meeting?",
          successtip: "✓ WHY = reason! Answer: 'I canceled it because I was sick' 🤒"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: '___ did you live before moving here?' B: 'I lived in Tokyo for 3 years.'",
          options: ["Where", "When", "Why"],
          a: "Where",
          successtip: "✓ WHERE! Geographic question! 🗼🌏"
        }
      ]
    },

    // ===================================================================
    // SET 9: MIXED TENSES MASTERY (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u4-s9",
      title: "Set 9: Past, Present, and Future",
      questions: [
        {
          type: "match",
          q: "Match the tenses:",
          pairs: [
            { left: "I worked (yesterday)", right: "Past simple" },
            { left: "I'm working (now)", right: "Present continuous" },
            { left: "I work (every day)", right: "Present simple" }
          ],
          a: "completed",
          successtip: "✓ Three different times! Past/Present continuous/Present simple! ⏰"
        },
        {
          type: "mc",
          q: "Five years ago I ___ as a barista. Now I ___ as a developer. Tomorrow I ___ to a conference!",
          options: ["worked / work / am going", "work / worked / go", "working / work / going"],
          a: "worked / work / am going",
          successtip: "✓ Career evolution! Past simple → Present simple → Future! ☕→💻"
        },
        {
          type: "input",
          q: "Last year she ___ TikTok videos for fun. Now she's a professional content creator! (create)",
          a: "created",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ CREATED! Hobby → Career! 📱💰"
        },
        {
          type: "order",
          q: "Traduce: Antes vivía en un apartamento, ahora vivo en una casa.",
          words: ["Before", "I", "lived", "in", "an", "apartment,", "now", "I", "live", "in", "a", "house"],
          a: "Before I lived in an apartment, now I live in a house",
          successtip: "✓ LIVED (past) vs LIVE (present)! Life upgrade! 🏢→🏠"
        },
        {
          type: "mc",
          q: "STORY: In 2020 everyone ___ from home. Now some people ___ remotely, others ___ to offices.",
          options: ["worked / work / are returning", "work / worked / returned", "working / work / return"],
          a: "worked / work / are returning",
          successtip: "✓ Past (2020) → Present habits → Present continuous (ongoing change)! 🏠💼"
        },
        {
          type: "input",
          q: "Yesterday I ___ 10 episodes. Today I'm taking a break. Tomorrow I'll finish the season! (watch)",
          a: "watched",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WATCHED! Three-day binge plan! 📺🍿"
        },
        {
          type: "match",
          q: "Technology evolution:",
          pairs: [
            { left: "In 2010 I used a flip phone", right: "Past" },
            { left: "Now I use a smartphone", right: "Present" },
            { left: "Yesterday I upgraded my phone", right: "Specific past" }
          ],
          a: "completed",
          successtip: "✓ Tech changes! General past/Present/Specific past event! 📱"
        },
        {
          type: "input",
          q: "CONTRAST: He usually walks to work, but yesterday he ___ because it was raining. (drive)",
          a: "drove",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["took a taxi", "took an uber"] },
          tip: "Drive is irregular (drove), but think of regular: 'called a taxi'!",
          successtip: "✓ Exception to routine! Weather changes plans! 🌧️🚗"
        },
        {
          type: "order",
          q: "Traduce: Antes no me gustaba el café, pero ahora lo tomo todos los días.",
          words: ["Before", "I", "didn't", "like", "coffee,", "but", "now", "I", "drink", "it", "every", "day"],
          a: "Before I didn't like coffee, but now I drink it every day",
          successtip: "✓ Tastes change! DIDN'T LIKE (past) → DRINK (present habit)! ☕"
        },
        {
          type: "mc",
          q: "COMPLEX: Last month I ___ to start exercising. Now I ___ to the gym 5 times a week, and yesterday I ___ for 2 hours!",
          options: ["decided / go / worked out", "decide / went / work out", "deciding / going / working"],
          a: "decided / go / worked out",
          successtip: "✓ 🎉 Three tenses! Decision (past) → Habit (present) → Specific action (past)! 💪"
        }
      ]
    },

    // ===================================================================
    // SET 10: DIARY/VLOG FORMAT (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u4-s10",
      title: "Set 10: My Day Yesterday",
      questions: [
        {
          type: "match",
          q: "READ VLOG: 'Hey guys! Yesterday was CRAZY! I woke up at 6am, worked out, showered, and started my new job!' Match the actions:",
          pairs: [
            { left: "Morning routine", right: "woke up, worked out, showered" },
            { left: "Big event", right: "started new job" },
            { left: "Format", right: "Vlog/casual" }
          ],
          a: "completed",
          successtip: "✓ Vlog = casual storytelling! Modern format! 📹"
        },
        {
          type: "mc",
          q: "DEAR DIARY: 'Today I ___ something I've never done before—I ___ sushi for the first time!'",
          options: ["tried / loved", "try / love", "trying / loving"],
          a: "tried / loved",
          tip: "Try → tried (y → ied!). Love → loved (add -d!)",
          successtip: "✓ New experience! Diary past tense! 🍣📓"
        },
        {
          type: "input",
          q: "FORMAL LOG: 'On Monday I attended a conference, on Tuesday I ___ with clients, and today I'm writing the report.' (meet)",
          a: "met",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["had meetings", "talked"] },
          tip: "Meet is irregular (met), but think regular: 'talked with clients'!",
          successtip: "✓ Professional tone! Formal register! 💼"
        },
        {
          type: "order",
          q: "VLOG: Traduce: Ayer fue un día normal. Trabajé desde casa y miré Netflix toda la noche.",
          words: ["Yesterday", "was", "a", "normal", "day.", "I", "worked", "from", "home", "and", "watched", "Netflix", "all", "night"],
          a: "Yesterday was a normal day. I worked from home and watched Netflix all night",
          successtip: "✓ Chill day! WAS (to be past) + WORKED + WATCHED! 🏠📺"
        },
        {
          type: "mc",
          q: "READ MINI-DIARY: 'Last weekend I visited my grandparents. We talked for hours, cooked together, and looked at old photos. It was special.' What made it special?",
          options: ["Quality time together", "Expensive activities", "New places"],
          a: "Quality time together",
          successtip: "✓ Simple but meaningful! Talked, cooked, looked = bonding! ❤️👴👵"
        },
        {
          type: "input",
          q: "VLOG: 'So yesterday I ___ to make bread. Guys, it was a DISASTER! I burned everything!' (try)",
          a: "tried",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ TRIED! Vlog fail = entertaining content! 🍞🔥😂"
        },
        {
          type: "match",
          q: "Register comparison:",
          pairs: [
            { left: "VLOG: 'I messed up!'", right: "Casual" },
            { left: "DIARY: 'I made a mistake'", right: "Neutral" },
            { left: "LOG: 'An error occurred'", right: "Formal" }
          ],
          a: "completed",
          successtip: "✓ Same meaning, different registers! Know your audience! 🗣️"
        },
        {
          type: "input",
          q: "JOURNAL: 'This morning I reflected on yesterday. I ___ some decisions I regret.' (make)",
          a: "made",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["had", "took"] },
          tip: "Make is irregular (made), but think: 'took some decisions'!",
          successtip: "✓ Reflective writing! Self-awareness! 📝💭"
        },
        {
          type: "order",
          q: "BLOG: Traduce: El mes pasado viajé a México. Visité Cancún y probé comida increíble.",
          words: ["Last", "month", "I", "traveled", "to", "Mexico.", "I", "visited", "Cancun", "and", "tried", "amazing", "food"],
          a: "Last month I traveled to Mexico. I visited Cancun and tried amazing food",
          tip: "Travel → traveled (one L in US). Visit → visited. Try → tried!",
          successtip: "✓ Travel blog style! Three past actions! 🇲🇽🌮"
        },
        {
          type: "mc",
          q: "FINAL VLOG: 'What a week! Monday I started my new job. Tuesday I ___ my old boss. Wednesday I ___  a welcome party. Thursday I ___ overwhelmed, but Friday I ___ great! Subscribe for more!'",
          options: ["called / attended / felt / felt", "call / attend / feel / feel", "calling / attending / feeling / feeling"],
          a: "called / attended / felt / felt",
          successtip: "✓ 🎉 PERFECTO! Week recap! All past simple! You're a storytelling pro! 🎬📱"
        }
      ]
    }
  ],

// ===================================================================
// READING SET: My Remote Work Experience
// ===================================================================
readingSet: {
  id: "m5-u4-reading",
  title: "Reading: My Remote Work Experience",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Beginning (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**My First Year Working from Home**

Last March I started working remotely. At first, I hated it. I missed my colleagues and the office energy. I worked in my pajamas (not recommended!) and ate lunch at my desk every day.`
    },
    {
      type: "mc",
      q: "When did the person start remote work?",
      options: ["Last March", "Yesterday", "At first"],
      a: "Last March",
      successtip: "✓ LAST MARCH! Specific past time! 🏠💻"
    },
    {
      type: "mc",
      q: "How did they feel at first?",
      options: ["Hated it", "Loved it", "Felt neutral"],
      a: "Hated it",
      successtip: "✓ HATED IT! But feelings changed! 😰→😊"
    },
    {
      type: "input",
      q: "What did they do in the beginning? They ___ in pajamas.",
      a: "worked",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ WORKED! Bad habit! 👔❌"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Making Changes (preguntas 4-5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Adjusting to the Change**

But things changed. I learned to create a routine. I started each morning with coffee and a walk. I dressed properly (yes, real pants!). I scheduled video calls with my team. I even decorated my home office.`
    },
    {
      type: "mc",
      q: "What changed their experience for the better?",
      options: ["Created a routine", "Quit the job", "Moved to office"],
      a: "Created a routine",
      successtip: "✓ Routine helped! Structure = success! 📅✅"
    },
    {
      type: "input",
      q: "Complete the sentence: They ___ their home office.",
      a: "decorated",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ DECORATED! Made it comfortable! 🖼️🪴"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: One Year Later (preguntas 6-9)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Results**

Yesterday marked my one-year anniversary. I realized something—I loved remote work now! I saved 2 hours of commute daily. I exercised more. I cooked healthy meals. I didn't miss traffic or crowded trains at all.`
    },
    {
      type: "mc",
      q: "When was their one-year anniversary?",
      options: ["Yesterday", "Last March", "Tomorrow"],
      a: "Yesterday",
      successtip: "✓ YESTERDAY! Time marker! 🎉"
    },
    {
      type: "mc",
      q: "How much commute time did they save daily?",
      options: ["2 hours", "1 hour", "30 minutes"],
      a: "2 hours",
      successtip: "✓ 2 HOURS! That's 10 hours per week! ⏰"
    },
    {
      type: "input",
      q: "Fill in the blank: They ___ healthy meals at home.",
      a: "cooked",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ COOKED! Healthier lifestyle! 🥗"
    },
    {
      type: "mc",
      q: "What part of commuting did they NOT miss?",
      options: ["Traffic and crowded trains", "Their colleagues", "The office"],
      a: "Traffic and crowded trains",
      successtip: "✓ No more commute stress! 🚗🚊"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Conclusion (pregunta 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Reflection**

Looking back, the transition wasn't easy, but it was worth it. My lifestyle is much better now than it was a year ago. Remote work requires discipline, but it offers a lot of freedom.`
    },
    {
      type: "mc",
      q: "COMPREHENSION: What is the main point of the story?",
      options: ["Remote work improved with time and routine", "Remote work is always bad", "Office work is better"],
      a: "Remote work improved with time and routine",
      successtip: "✓ Perfect understanding! Adaptation = key! 🏆"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u4-review",
    title: "Unit 4 Review: Past Simple Mastery",
    questions: [
      {
        type: "match",
        q: "Match present to past:",
        pairs: [
          { left: "I watch", right: "I watched" },
          { left: "She studies", right: "She studied" },
          { left: "They work", right: "They worked" }
        ],
        a: "completed",
        successtip: "✓ Regular verbs: add -ed (or -ied for y endings)! ✅"
      },
      {
        type: "mc",
        q: "Yesterday I ___ for 3 hours.",
        options: ["studied", "study", "studying"],
        a: "studied",
        successtip: "✓ STUDIED! Past simple with time marker! 📚"
      },
      {
        type: "input",
        q: "She ___ on Instagram last night. (post)",
        a: "posted",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ POSTED! Social media action! 📱"
      },
      {
        type: "mc",
        q: "Negative: I ___ TV yesterday.",
        options: ["didn't watch", "didn't watched", "not watched"],
        a: "didn't watch",
        successtip: "✓ DIDN'T + BASE VERB! No -ed after didn't! ❌"
      },
      {
        type: "order",
        q: "Make a question:",
        words: ["Did", "you", "work", "yesterday?"],
        a: "Did you work yesterday?",
        successtip: "✓ Did + subject + base verb! ❓"
      },
      {
        type: "input",
        q: "A: 'Did they enjoy the party?' B: 'Yes, they ___.'",
        a: "did",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ DID! Short answer! 🎉"
      },
      {
        type: "mc",
        q: "___ did you watch last night?",
        options: ["What", "Where", "Who"],
        a: "What",
        successtip: "✓ WHAT! Information question! 📺"
      },
      {
        type: "match",
        q: "Time expressions:",
        pairs: [
          { left: "Yesterday", right: "Past" },
          { left: "Last week", right: "Past" },
          { left: "Two days ago", right: "Past" }
        ],
        a: "completed",
        successtip: "✓ Time markers help identify past simple! ⏰"
      },
      {
        type: "input",
        q: "Last year I ___ in New York. Now I live in LA. (live)",
        a: "lived",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ LIVED! Past vs present! 🗽→🌴"
      },
      {
        type: "mc",
        q: "FINAL: Complete the story: 'Yesterday ___ amazing! I ___ up early, ___ to the gym, and ___ a healthy breakfast. Then I ___ on my project and ___ great progress!'",
        options: ["was / woke / walked / cooked / worked / made", "is / wake / walk / cook / work / make", "being / waking / walking / cooking / working / making"],
        a: "was / woke / walked / cooked / worked / made",
        tip: "Mix of to be (was) + regular (walked, worked, cooked) + irregular (woke, made)!",
        successtip: "✓ 🎉 PERFECTO! You mastered past simple! Productive day story! 🏆💪"
      }
    ]
  }
};
